import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { euro } from '../../lib/format';
import { useLiveOrders } from '../../lib/useLiveOrders';
import { socket } from '../../lib/socket';
import { useToasts } from '../../lib/toast';
import { useLang } from '../../lib/lang';
import { useDevice } from '../../store';
import { LoginGate } from '../LoginGate';
import { Kiosk } from '../kiosk/Kiosk';
import { ScanModal } from '../../lib/ScanModal';
import { parseTicketQr } from '../../lib/Qr';
import { ClotureManager } from '../backoffice/ClotureManager';
import { ReservationsManager } from '../backoffice/ReservationsManager';
import type { OrderDto, OrderLineDto, OrderType, PaymentMethod, RestaurantInfo, ReservationRow, TerminalPaymentDto } from '@taj/types';

/** Heure demandée par le client, en HH:MM. */
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Commande du site où le client devait payer par carte en ligne mais n'a rien réglé.
 * Les premières minutes il est peut-être encore sur la page de paiement → on n'alarme
 * qu'au-delà de 10 minutes, sinon on indique simplement que le paiement est en cours.
 */
const onlinePayState = (o: OrderDto): 'none' | 'pending' | 'abandoned' => {
  if (!o.onlinePayExpected || o.paidCents > 0) return 'none';
  const age = Date.now() - new Date(o.createdAt).getTime();
  return age > 10 * 60_000 ? 'abandoned' : 'pending';
};

const METHODS: { m: PaymentMethod; label: string }[] = [
  { m: 'CASH', label: '💵 Espèces' },
  { m: 'CARD', label: '💳 Carte Bleue & Carte ticket' },
  { m: 'TICKET_RESTO', label: '🎟️ Ticket papier' },
];
const lineTotal = (l: OrderLineDto) =>
  (l.unitPriceCents + l.options.reduce((s, o) => s + o.priceDelta, 0)) * l.qty;

const PAY_LABEL: Record<string, string> = {
  CASH: '💵 Espèces', CARD: '💳 Carte Bleue & Carte ticket', TICKET_RESTO: '🎟️ Ticket papier',
  CARTE_TICKET: '💳 Carte Bleue & Carte ticket', MEAL_VOUCHER: '🍽️ Titre-resto', ONLINE: '🏦 En ligne',
};
const CHANNEL: Record<string, string> = {
  KIOSK: '🛎️ Borne', ONLINE: '🌐 Site web', CASHIER: '💳 Caisse', UBER_EATS: 'Uber Eats', DELIVEROO: 'Deliveroo',
};
const itemsSummary = (o: OrderDto) =>
  o.lines.map((l) => `${l.qty > 1 ? l.qty + '× ' : ''}${l.nameSnap}`).join(', ');

// Bip d'alerte (2 notes) quand une nouvelle commande arrive — via Web Audio, sans fichier son.
let _audioCtx: AudioContext | null = null;
function beepNewOrder() {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    _audioCtx = _audioCtx ?? new AC();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') void ctx.resume();
    const now = ctx.currentTime;
    [0, 0.2].forEach((off) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, now + off);
      gain.gain.exponentialRampToValueAtTime(0.35, now + off + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + off + 0.16);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + off);
      osc.stop(now + off + 0.18);
    });
  } catch { /* audio indisponible — on ignore */ }
}

export function Cashier() {
  const auth = useDevice((s) => s.auth);
  const resetRole = useDevice((s) => s.resetRole);
  const setRole = useDevice((s) => s.setRole);
  const push = useToasts((s) => s.push);
  const { orders, live } = useLiveOrders('cashier');
  const [search, setSearch] = useState('');
  const [land, setLand] = useState(false); // une seule page (portrait) par défaut
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);

  // 🔔 Notification quand une nouvelle commande du site arrive dans la caisse (bip + toast).
  useEffect(() => {
    const onNew = (o: OrderDto) => {
      if (o.channel !== 'ONLINE') return;
      beepNewOrder();
      const fr = useLang.getState().lang === 'fr';
      push(`🔔 ${fr ? 'Nouvelle commande en ligne' : 'New online order'} · ${o.token ?? '#' + o.number} · ${o.clientName ?? ''}`);
    };
    socket.on('order:new', onNew);
    return () => { socket.off('order:new', onNew); };
  }, [push]);

  // 🗓️ Notification quand une nouvelle réservation arrive du site (bip + toast).
  useEffect(() => {
    const onResa = (r: ReservationRow) => {
      if (r.status === 'PENDING') setPendingResa((n) => n + 1); // badge persistant qui reste jusqu'au traitement
      beepNewOrder();
      const fr = useLang.getState().lang === 'fr';
      const [y, m, d] = (r.date || '').split('-');
      const dt = d ? `${d}/${m}` : r.date;
      push(`🗓️ ${fr ? 'Nouvelle réservation' : 'New reservation'} · ${r.name} · ${r.partySize} ${fr ? 'pers.' : 'guests'} · ${dt} ${r.time}`);
    };
    socket.on('reservation:new', onResa);
    return () => { socket.off('reservation:new', onResa); };
  }, [push]);

  // Compte des réservations « en attente » → badge rouge persistant sur le bouton (reste jusqu'au traitement).
  const loadPendingResa = () => {
    api.listReservations().then((rs) => setPendingResa(rs.filter((r) => r.status === 'PENDING').length)).catch(() => {});
  };
  useEffect(() => { if (auth) loadPendingResa(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [auth]);

  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const typeLabel = (ty: string) => (({
    DINE_IN: '🍽 ' + t('Sur place', 'Dine in'),
    TAKEAWAY: '🛍 ' + t('À emporter', 'Takeaway'),
    DELIVERY: '🛵 ' + t('Livraison', 'Delivery'),
    CLICK_COLLECT: '📦 ' + t('Click & Collect', 'Click & Collect'),
  } as Record<string, string>)[ty] ?? ty);

  if (!auth) return <LoginGate mode="pin" />;

  const q = search.trim().toLowerCase();
  const match = (o: OrderDto) =>
    !q || String(o.number).includes(q) || (o.clientName ?? '').toLowerCase().includes(q) || (o.token ?? '').toLowerCase().includes(q);
  const pending = orders.filter((o) => o.status === 'PENDING_PAYMENT' && match(o));
  const tracking = orders.filter((o) => o.status !== 'PENDING_PAYMENT' && match(o));

  const soon = () => push(t('Bientôt disponible 🚧', 'Coming soon 🚧'), 'warn');
  const [modal, setModal] = useState<{ kind: 'pay' | 'items' | 'void' | 'justif' | 'detail'; order: OrderDto } | null>(null);
  const [take, setTake] = useState<{ type: OrderType; delivery: boolean; editOrder?: OrderDto } | null>(null); // prise / modification de commande
  const [scanOpen, setScanOpen] = useState(false);
  const [cloture, setCloture] = useState(false); // clôture de caisse / Ticket Z (fin de service)
  const [reservations, setReservations] = useState(false); // panneau réservations de table
  const [pendingResa, setPendingResa] = useState(0); // nb de réservations en attente (badge persistant)
  const [syncing, setSyncing] = useState(false);

  /**
   * Synchronise l'appareil : récupère la dernière version de l'application (nouveau code) et,
   * au rechargement, les derniers changements du menu / prix / réglages. À utiliser après une
   * modification faite au back-office si l'écran ne s'est pas mis à jour tout seul.
   */
  const syncApp = async () => {
    setSyncing(true);
    push(t('🔄 Synchronisation en cours…', '🔄 Syncing…'));
    try {
      const regs = await navigator.serviceWorker?.getRegistrations?.();
      await Promise.all((regs ?? []).map((r) => r.update())); // cherche une version plus récente
      const keys = await caches?.keys?.();
      await Promise.all((keys ?? []).map((k) => caches.delete(k))); // purge les anciens fichiers en cache
    } catch { /* pas de service worker : on recharge quand même */ }
    location.reload(); // recharge → nouvelle version + menu/prix/réglages à jour
  };

  // Scan d'un ticket → ouvre la commande en caisse (la rouvre si déjà fermée).
  // Deux codes sont imprimés sur le ticket : le QR (identifiant complet) et le code-barres (n° de commande).
  const handleScan = async (text: string) => {
    setScanOpen(false);
    const id = parseTicketQr(text);
    const num = !id && /^\d{1,7}$/.test(text.trim()) ? Number(text.trim()) : null;
    if (!id && num === null) return push(t('Code non reconnu', 'Code not recognized'), 'warn');
    try {
      let o = id ? await api.getOrder(id) : await api.getOrderByNumber(num!);
      if (o.status === 'COMPLETED' || o.status === 'CANCELLED') {
        o = await api.reopenOrder(o.id);
        push(`🔓 ${t('Commande rouverte', 'Order reopened')} · ${o.token ?? '#' + o.number}`);
      }
      setSearch(String(o.number)); // fait remonter la carte (avec toutes ses actions) dans la liste
      const paid = o.paidCents >= o.totalCents;
      setModal({ kind: paid ? 'justif' : 'pay', order: o }); // payé → aperçu ticket ; non payé → encaissement
    } catch (e: any) {
      push(t('Ticket introuvable', 'Order not found') + (e?.message ? ` : ${e.message}` : ''), 'warn');
    }
  };

  // ── Douchette USB (lecteur code-barres/QR à main) ──
  // Une douchette agit comme un clavier : elle « tape » le contenu du QR (TAJ:<id>) très vite
  // puis envoie Entrée. On capte cette saisie rapide au niveau de la fenêtre et on ouvre la commande,
  // sans qu'il faille cliquer sur quoi que ce soit. Déclenché uniquement sur le préfixe TAJ: → n'interfère
  // pas avec la frappe normale (recherche, montants…).
  const handleScanRef = useRef(handleScan);
  handleScanRef.current = handleScan;
  useEffect(() => {
    let buf = '';
    let last = 0;
    let flush: ReturnType<typeof setTimeout> | undefined;
    const isTicket = (s: string) => s.startsWith('TAJ:') || /\/suivi\//i.test(s) || /^[A-Za-z0-9]{20,}$/.test(s);
    // Le code-barres du ticket ne contient que le n° de commande : on ne l'accepte que si la frappe
    // ne vient pas d'un champ de saisie, pour ne pas confondre avec un caissier qui tape un montant.
    const typing = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onKey = (e: KeyboardEvent) => {
      const now = Date.now();
      if (now - last > 120) buf = ''; // grand écart = nouvelle saisie (frappe humaine)
      last = now;
      if (flush) clearTimeout(flush);
      if (e.key === 'Enter') {
        const barcode = /^\d{2,7}$/.test(buf) && !typing(e);
        if (isTicket(buf) || barcode) { e.preventDefault(); const code = buf; buf = ''; handleScanRef.current(code); }
        else buf = '';
        return;
      }
      if (e.key.length === 1) {
        buf += e.key;
        // Repli si la douchette n'ajoute pas Entrée : on valide après une courte pause.
        if (buf.startsWith('TAJ:') || buf.includes('/suivi/')) {
          flush = setTimeout(() => { const code = buf; buf = ''; if (isTicket(code)) handleScanRef.current(code); }, 200);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (flush) clearTimeout(flush); };
  }, []);

  const closeOrder = async (o: OrderDto) => {
    const paid = o.paidCents >= o.totalCents;
    if (!paid && !confirm(t('Cette commande n’est pas payée. La fermer quand même ?', 'This order is unpaid. Close it anyway?'))) return;
    if (paid && !confirm(t(`Fermer la commande ${o.token} ?`, `Close order ${o.token}?`))) return;
    try {
      await api.completeOrder(o.id);
      push(`✓ ${t('Commande fermée', 'Order closed')} · ${o.token}`);
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const cancelOrder = async (o: OrderDto) => {
    if (!confirm(t(`Annuler la commande ${o.token} ? Elle sera tracée sur le Ticket Z.`, `Cancel order ${o.token}? It will be logged on the Z ticket.`))) return;
    const reason = prompt(t('Motif de l’annulation (facultatif) :', 'Cancellation reason (optional):')) ?? undefined;
    try {
      await api.cancelOrder(o.id, reason || undefined);
      push(`🗑️ ${t('Commande annulée', 'Order cancelled')} · ${o.token}`);
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const confirmPay = async (o: OrderDto) => {
    const method = (o.paymentIntent && o.paymentIntent !== 'ONLINE' ? o.paymentIntent : 'CASH') as PaymentMethod;
    try {
      await api.addPayment({ orderId: o.id, method, amountCents: o.totalCents });
      push(`💚 ${t('Encaissé', 'Paid')} ${euro(o.totalCents)} · ${PAY_LABEL[method]} → cuisine`);
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  // Valider une commande du site → l'envoyer en cuisine sans encaisser (paiement au retrait / livraison).
  const validateKitchen = async (o: OrderDto) => {
    try {
      await api.sendToKitchen(o.id);
      push(`👨‍🍳 ${t('Commande validée → cuisine', 'Order sent to kitchen')} · ${o.token}`);
    } catch (e: any) { push(e.message, 'warn'); }
  };

  return (
    <div className="cs">
      {/* ── Top bar ── */}
      <div className="cs-top">
        <div className="cs-brand">
          <span className="cs-ic">💳</span>
          <div><div className="cs-staff">STAFF</div><div className="cs-role">{t('Caissier', 'Cashier')}</div></div>
        </div>
        <div className="cs-controls">
          <div className="cs-seg">
            <button className={!land ? 'on' : ''} onClick={() => setLand(false)}>📱 {t('Portrait', 'Portrait')}</button>
            <button className={land ? 'on' : ''} onClick={() => setLand(true)}>🖥️ {t('Paysage', 'Landscape')}</button>
          </div>
          <button
            className="cs-btn"
            title={t('Récupère la dernière version et les derniers changements (menu, prix, réglages).', 'Fetch the latest version and changes (menu, prices, settings).')}
            disabled={syncing}
            onClick={syncApp}
          >🔄 {syncing ? t('Mise à jour…', 'Updating…') : t('Synchroniser', 'Sync')}</button>
          <button
            className="cs-btn"
            onClick={() => {
              if (confirm(t('Ouvrir le back-office (réglages) ? Connexion administrateur requise.', 'Open back-office (settings)? Admin login required.'))) {
                setRole('BACKOFFICE');
              }
            }}
          >⚙️ {t('Général', 'General')}</button>
          <span className="cs-user">👤 {auth.staff.fullName}</span>
          <div className="cs-lang">
            <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button>
            <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
          </div>
          <span className="live-pill"><span className={`ldot${live ? '' : ' off'}`} />{live ? t('EN DIRECT', 'LIVE') : t('HORS LIGNE', 'OFFLINE')}</span>
        </div>
      </div>

      <div className="cs-main">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="cs-take" style={{ flex: 2, width: 'auto', marginTop: 0, padding: '12px 16px', fontSize: '1.1rem' }} onClick={() => setTake({ type: 'DINE_IN', delivery: false })}>📋 {t('Prendre une commande', 'Take an order')}</button>
          <button className="cs-take" style={{ flex: 1, width: 'auto', marginTop: 0, padding: '12px 16px', fontSize: '1.1rem', background: '#2a6fb0' }} onClick={() => setScanOpen(true)}>📷 {t('Scanner (caméra)', 'Scan (camera)')}</button>
          <button className="cs-take" style={{ flex: 1, width: 'auto', marginTop: 0, padding: '12px 16px', fontSize: '1.1rem', background: '#8a5cc0' }} onClick={() => setCloture(true)}>🧾 {t('Clôture de caisse', 'Close register')}</button>
          <button className="cs-take" style={{ flex: 1, width: 'auto', marginTop: 0, padding: '12px 16px', fontSize: '1.1rem', background: pendingResa > 0 ? '#c0392b' : '#1f7a6f', position: 'relative' }} onClick={() => setReservations(true)}>
            📅 {t('Réservations', 'Reservations')}
            {pendingResa > 0 && (
              <span style={{ position: 'absolute', top: -9, right: -9, minWidth: 26, height: 26, borderRadius: 13, background: '#fff', color: '#c0392b', fontWeight: 900, fontSize: '.9rem', display: 'grid', placeItems: 'center', padding: '0 6px', border: '2px solid #c0392b' }}>{pendingResa}</span>
            )}
          </button>
          <span
            title={t('Scannez le QR d’un ticket avec la douchette : la commande s’ouvre automatiquement.', 'Scan a ticket QR with the handheld scanner: the order opens automatically.')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 12px', borderRadius: 10, background: '#eaf3fb', color: '#2a6fb0', fontWeight: 700, whiteSpace: 'nowrap' }}
          >🔫 {t('Douchette prête', 'Scanner ready')}</span>
        </div>
        <div className="cs-search">
          🔍 <input
            placeholder={t('Rechercher : n° commande, nom, téléphone…', 'Search: order n°, name, phone…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="cs-body">
          {/* ── EN ATTENTE DE PAIEMENT ── */}
          <div className="cs-col">
            <div className="cs-sec green">{t('En attente de paiement', 'Awaiting payment')} <span className="cs-count">{pending.length}</span></div>
            {pending.length === 0 && <div className="no-data">{t('Rien à encaisser', 'Nothing to charge')}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: land ? 'repeat(auto-fill, minmax(260px, 1fr))' : '1fr', gap: 10, alignItems: 'start' }}>
            {pending.map((o) => (
              <div key={o.id} className={`csp${o.paidCents >= o.totalCents ? ' csp-paid' : ''}`}>
                <div className="csp-head">
                  <span className="csp-token">{o.token}</span>
                  <div className="csp-info">
                    <div className="csp-name">{o.channel === 'ONLINE' ? '🌐 ' : ''}{o.clientName}</div>
                    <div className="csp-meta">#{o.number} · {typeLabel(o.type)}{o.channel === 'ONLINE' ? ' · ' + t('EN LIGNE', 'ONLINE') : ''}{o.paymentIntent ? ' · ' + PAY_LABEL[o.paymentIntent].replace(/^\S+ /, '') : ''}</div>
                    {/* Coordonnées : appeler le client d'un appui, adresse pour le livreur. */}
                    {(o.clientPhone || o.deliveryAddress) && (
                      <div className="cs-contact">
                        {o.clientPhone && <a className="cs-tel" href={`tel:${o.clientPhone}`}>📞 {o.clientPhone}</a>}
                        {o.deliveryAddress && <span className="cs-adr">📍 {o.deliveryAddress}</span>}
                      </div>
                    )}
                    {o.wantedAt && <div className="cs-wanted">⏰ {t('pour', 'for')} {hhmm(o.wantedAt)}{o.scheduledHold ? ` · ${t('en attente cuisine', 'held for kitchen')}` : ''}</div>}
                    {/* Déjà réglé en ligne : le caissier ne doit surtout pas encaisser une 2e fois. */}
                    {o.paidCents >= o.totalCents && (
                      <div className="cs-onlinepaid">
                        ✅ {t('Déjà payé en ligne — ne rien encaisser, valider en cuisine', 'Already paid online — do not charge, send to kitchen')}
                      </div>
                    )}
                    {/* Paiement carte en ligne annoncé mais jamais réglé. */}
                    {onlinePayState(o) === 'abandoned' && (
                      <div className="cs-abandon">🔴 {t('Paiement carte abandonné — le client n’a pas payé en ligne', 'Card payment abandoned — customer never paid online')}</div>
                    )}
                    {onlinePayState(o) === 'pending' && (
                      <div className="cs-paying">🕐 {t('Paiement en ligne en cours…', 'Online payment in progress…')}</div>
                    )}
                  </div>
                  <div className="csp-amt"><span className="csp-badge" style={o.paidCents >= o.totalCents ? { color: '#1f8a4d' } : undefined}>{o.paidCents >= o.totalCents ? t('✓ PAYÉ EN LIGNE', '✓ PAID ONLINE') : t('À RÉGLER', 'TO PAY')}</span><span className="csp-total">{euro(o.totalCents)}</span></div>
                  <button className="cs-eye" title={t('Voir le détail avant d’encaisser', 'View details before charging')} onClick={() => setModal({ kind: 'detail', order: o })}>👁️</button>
                </div>
                <div className="csp-items">{itemsSummary(o)}</div>
                {o.channel === 'ONLINE' && o.status === 'PENDING_PAYMENT' && (
                  <button className="cs-confirm" style={{ background: '#1f7a4d', marginBottom: 6 }} onClick={() => validateKitchen(o)}>
                    👨‍🍳 {t('Valider → envoyer en cuisine', 'Validate → send to kitchen')}
                  </button>
                )}
                {/* Commande déjà réglée en ligne : on retire tout ce qui encaisserait une 2e fois. */}
                {o.paidCents < o.totalCents && (
                  <>
                    <button className="cs-act" onClick={soon}>🏷️ {t('Promo', 'Discount')}</button>
                    <button className="cs-act" onClick={() => setModal({ kind: 'pay', order: o })}>👥 {t('Partager l’addition', 'Split bill')}</button>
                    <button className="cs-act" onClick={() => setModal({ kind: 'items', order: o })}>🍽 {t('Payer certains plats', 'Pay some items')}</button>
                  </>
                )}
                <div className="csp-foot">
                  <button className="cs-modif" onClick={() => setTake({ type: o.type, delivery: o.type === 'DELIVERY', editOrder: o })}>✏️ {t('Modifier', 'Edit')}</button>
                  <button className="cs-modif" style={{ color: '#c0392b' }} onClick={() => cancelOrder(o)}>🗑️ {t('Annuler', 'Cancel')}</button>
                  {o.paidCents < o.totalCents && <button className="cs-confirm" onClick={() => confirmPay(o)}>✓ {t('CONFIRMER ENCAISSEMENT', 'CONFIRM PAYMENT')}</button>}
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* ── SUIVI DES COMMANDES ── */}
          <div className="cs-col">
            <div className="cs-sec">{t('Suivi des commandes', 'Order tracking')} <span className="cs-count gray">{tracking.length}</span></div>
            {tracking.length === 0 && <div className="no-data">—</div>}
            <div style={{ display: 'grid', gridTemplateColumns: land ? 'repeat(auto-fill, minmax(260px, 1fr))' : '1fr', gap: 10, alignItems: 'start' }}>
            {tracking.map((o) => {
              const paid = o.paidCents >= o.totalCents;
              const partial = !paid && o.paidCents > 0; // en cours de paiement (partagé)
              return (
                <div key={o.id} className={`cst${paid ? ' cst-paid' : partial ? ' cst-partial' : ''}`}>
                  <div className="cst-head">
                    <span className="cst-token">{o.token}</span>
                    <div className="cst-info">
                      <div className="cst-top">
                        <b>{o.channel === 'ONLINE' ? CHANNEL.ONLINE + ' · ' : ''}{o.clientName}</b>
                        <span className="cst-amt">{euro(o.totalCents)}</span>
                        <span className={`stat-pill ${o.status}`}>{o.status === 'RECEIVED' ? t('REÇUE', 'RECEIVED') : o.status}</span>
                      </div>
                      <div className="cst-items">{typeLabel(o.type)}{o.deliveryAddress ? ' · 📍 ' + o.deliveryAddress : ''} · {itemsSummary(o)}</div>
                      {o.clientPhone && (
                        <div className="cs-contact"><a className="cs-tel" href={`tel:${o.clientPhone}`}>📞 {o.clientPhone}</a></div>
                      )}
                      {o.wantedAt && <div className="cs-wanted">⏰ {t('pour', 'for')} {hhmm(o.wantedAt)}</div>}
                    </div>
                  </div>
                  <div className="cst-actions">
                    <button className="cs-mini" onClick={() => setModal({ kind: 'detail', order: o })}>👁️ {t('Voir', 'View')}</button>
                    <button className="cs-mini" onClick={() => setModal({ kind: 'justif', order: o })}>📄 {t('Justificatif', 'Receipt')}</button>
                    <button
                      className="cs-mini"
                      onClick={() => {
                        if (paid && !confirm(t('Cette commande est déjà payée. La rouvrir pour ajouter / modifier des plats ? Vous encaisserez la différence.', 'This order is already paid. Reopen to add / edit items? You will charge the difference.'))) return;
                        setTake({ type: o.type, delivery: o.type === 'DELIVERY', editOrder: o });
                      }}
                    >➕ {t('Ajouter / Modifier', 'Add / Edit')}</button>
                    {!paid && <button className="cs-mini" style={{ color: '#c0392b' }} onClick={() => cancelOrder(o)}>🗑️ {t('Annuler', 'Cancel')}</button>}
                  </div>
                  <div className="cst-pay">
                    {paid ? '✔ ' + t('Payé', 'Paid') + ' · ' + PAY_LABEL[o.paymentIntent ?? 'CASH'] : partial ? '🟠 ' + t('Reste', 'Left') + ' ' + euro(o.totalCents - o.paidCents) : '⏳ ' + t('Non réglé', 'Unpaid')}
                    {paid && <button className="cs-link" onClick={() => setModal({ kind: 'void', order: o })}>✏️ {t('Modifier le paiement', 'Change payment')}</button>}
                  </div>
                  {!paid && (
                    <button className="cs-confirm" style={{ marginTop: 8 }} onClick={() => setModal({ kind: 'pay', order: o })}>💵 {t('Encaisser', 'Charge')} · {euro(o.totalCents)}</button>
                  )}
                  <button className="cs-confirm" style={{ marginTop: 8, background: paid ? undefined : '#b98' }} onClick={() => closeOrder(o)}>✓ {t('Fermer la commande', 'Close order')}</button>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        <button className="cs-device" onClick={() => confirm(t('Changer le rôle de cet appareil ?', 'Change this device role?')) && resetRole()}>⊙ {t('Caisse', 'Cashier')}</button>
      </div>

      {modal && (
        modal.kind === 'void' ? (
          <VoidModal order={modal.order} t={t} onClose={() => setModal(null)} />
        ) : modal.kind === 'justif' ? (
          <JustifModal order={modal.order} t={t} onClose={() => setModal(null)} />
        ) : modal.kind === 'detail' ? (
          <DetailModal order={modal.order} t={t} onClose={() => setModal(null)} />
        ) : (
          <PaymentModal kind={modal.kind} order={modal.order} t={t} onClose={() => setModal(null)} />
        )
      )}

      {take && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#fff', overflow: 'auto' }}>
          <Kiosk mode="cashier" delivery={take.delivery} startType={take.type} editOrder={take.editOrder} onExit={() => setTake(null)} />
        </div>
      )}

      {/* Clôture de caisse (Ticket Z) — accessible directement depuis la caisse, en fin de service. */}
      {cloture && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#f6efe1', overflow: 'auto', padding: 16 }}>
          <ClotureManager onBack={() => setCloture(false)} />
        </div>
      )}

      {/* Réservations de table — arrivent du site, à accepter/refuser ici. */}
      {reservations && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#f6efe1', overflow: 'auto', padding: 16 }}>
          <ReservationsManager onBack={() => { setReservations(false); loadPendingResa(); }} />
        </div>
      )}

      {scanOpen && (
        <ScanModal
          title={t('Scanner le QR du ticket', 'Scan the ticket QR')}
          onScan={handleScan}
          onClose={() => setScanOpen(false)}
        />
      )}
    </div>
  );
}

// ───────────────────────── Fenêtres de paiement ─────────────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(40,20,10,.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16,
};
const sheet: React.CSSProperties = {
  background: '#fffdf8', borderRadius: 18, padding: 22, width: 'min(460px,95vw)',
  maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)',
};

type TF = (fr: string, en: string) => string;

/** Encaissement mixte / partagé (plusieurs paiements partiels jusqu'au solde). */
function PaymentModal({ kind, order, t, onClose }: { kind: 'pay' | 'items'; order: OrderDto; t: TF; onClose: () => void }) {
  const push = useToasts((s) => s.push);
  const [remaining, setRemaining] = useState(Math.max(0, order.totalCents - order.paidCents));
  const [sel, setSel] = useState<string[]>([]);
  const [amount, setAmount] = useState(((order.totalCents - order.paidCents) / 100).toFixed(2));
  const [parts, setParts] = useState(2);

  const [itemParts, setItemParts] = useState(1); // partage d'un plat entre N personnes
  const [total, setTotal] = useState(order.totalCents); // total courant (change si remise)
  const [discOpen, setDiscOpen] = useState(false);
  const [discType, setDiscType] = useState<'PERCENT' | 'AMOUNT'>('PERCENT');
  const [discVal, setDiscVal] = useState('');
  const selSum = order.lines.filter((l) => sel.includes(l.id)).reduce((s, l) => s + lineTotal(l), 0);
  // Part à encaisser maintenant = total sélectionné divisé par le nombre de personnes qui le partagent.
  const itemsShare = itemParts > 1 ? Math.round(selSum / itemParts) : selSum;
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const setShare = (n: number) => { setParts(n); setAmount((remaining / n / 100).toFixed(2)); };

  /** Applique une remise (% ou €) à la commande puis met à jour le total / reste. */
  const applyDiscount = async () => {
    const raw = parseFloat(discVal.replace(',', '.'));
    if (!raw || raw <= 0) return push(t('Valeur de remise invalide', 'Invalid discount'), 'warn');
    const value = discType === 'PERCENT' ? Math.round(raw) : Math.round(raw * 100);
    try {
      const o = await api.applyDiscount(order.id, { type: discType, value });
      setTotal(o.totalCents);
      const rem = Math.max(0, o.totalCents - o.paidCents);
      setRemaining(rem);
      setAmount((rem / 100).toFixed(2));
      setDiscOpen(false); setDiscVal('');
      push(`🏷️ ${t('Remise appliquée', 'Discount applied')} · ${t('nouveau total', 'new total')} ${euro(o.totalCents)}`);
    } catch (e: any) { push(e.message, 'warn'); }
  };

  // ── Encaissement par terminal (SumUp) ──
  const [terminalOk, setTerminalOk] = useState(false);
  const [term, setTerm] = useState<TerminalPaymentDto | null>(null);
  useEffect(() => {
    api.terminalAvailability('Caisse').then((a) => setTerminalOk(a.available)).catch(() => setTerminalOk(false));
  }, []);

  /** Envoie le montant sur le terminal de la caisse, puis suit le paiement jusqu'à son issue. */
  const payTerminal = async () => {
    const cents = kind === 'items' ? itemsShare : Math.round(parseFloat(amount.replace(',', '.')) * 100);
    if (!cents || cents <= 0) return push(t('Montant invalide', 'Invalid amount'), 'warn');
    try { setTerm(await api.startTerminalPayment(order.id, 'Caisse', cents)); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  // Interrogation du terminal tant que le client n'a pas présenté sa carte.
  useEffect(() => {
    if (!term || term.status !== 'PENDING' || !term.id) return;
    const timer = setInterval(async () => {
      try {
        const next = await api.terminalPaymentStatus(term.id);
        setTerm(next);
        if (next.status === 'PAID') {
          clearInterval(timer);
          const o = next.order;
          if (o && o.paidCents >= o.totalCents) { push(`💚 ${t('Commande soldée', 'Fully paid')} → cuisine`); onClose(); return; }
          const rest = o ? o.totalCents - o.paidCents : 0;
          setRemaining(rest);
          setAmount((rest / 100).toFixed(2));
          if (!(kind === 'items' && itemParts > 1)) setSel([]);
          setTerm(null);
          push(`✅ ${t('Encaissé par carte', 'Paid by card')} ${euro(next.amountCents)} · ${t('reste', 'left')} ${euro(rest)}`);
        }
      } catch { /* réseau : nouvelle tentative au prochain tour */ }
    }, 2000);
    return () => clearInterval(timer);
  }, [term?.id, term?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelTerm = async () => {
    if (!term) return;
    try { if (term.id) await api.cancelTerminalPayment(term.id); } catch { /* le terminal a peut-être déjà rendu la main */ }
    setTerm(null);
  };

  const pay = async (method: PaymentMethod) => {
    const cents = kind === 'items' ? itemsShare : Math.round(parseFloat(amount.replace(',', '.')) * 100);
    if (!cents || cents <= 0) return push(t('Montant invalide', 'Invalid amount'), 'warn');
    try {
      const res = await api.addPayment({ orderId: order.id, method, amountCents: cents, ...(kind === 'items' ? { lineIds: sel } : {}) });
      if (res.fullyPaid) { push(`💚 ${t('Commande soldée', 'Fully paid')} → cuisine`); onClose(); return; }
      setRemaining(res.remainingCents);
      // En mode partage, on garde la sélection pour encaisser la personne suivante.
      if (!(kind === 'items' && itemParts > 1)) setSel([]);
      setAmount((res.remainingCents / 100).toFixed(2));
      push(`✅ ${t('Encaissé', 'Paid')} ${euro(cents)} · ${t('reste', 'left')} ${euro(res.remainingCents)}`);
    } catch (e: any) { push(e.message, 'warn'); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <b style={{ fontSize: '1.2rem', flex: 1 }}>{kind === 'items' ? t('Payer certains plats', 'Pay some items') : t('Paiement mixte / partagé', 'Split payment')}</b>
          <button className="cs-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ color: '#777', marginBottom: 10 }}>{order.token} · {t('Reste à payer', 'Remaining')} <b style={{ color: '#c60' }}>{euro(remaining)}</b> / {euro(total)}</div>

        {/* Remise à l'encaissement (% ou montant). */}
        <div style={{ marginBottom: 12 }}>
          {!discOpen ? (
            <button className="cs-act" style={{ margin: 0, width: 'auto', padding: '8px 14px' }} onClick={() => setDiscOpen(true)}>🏷️ {t('Remise', 'Discount')}</button>
          ) : (
            <div style={{ background: '#f7f1e4', borderRadius: 10, padding: 10 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button className="cs-act" style={{ margin: 0, flex: 1, fontWeight: discType === 'PERCENT' ? 900 : 600, opacity: discType === 'PERCENT' ? 1 : 0.55 }} onClick={() => setDiscType('PERCENT')}>% {t('Pourcent', 'Percent')}</button>
                <button className="cs-act" style={{ margin: 0, flex: 1, fontWeight: discType === 'AMOUNT' ? 900 : 600, opacity: discType === 'AMOUNT' ? 1 : 0.55 }} onClick={() => setDiscType('AMOUNT')}>€ {t('Montant', 'Amount')}</button>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="inp" inputMode="decimal" autoFocus placeholder={discType === 'PERCENT' ? '10' : '5,00'} value={discVal} onChange={(e) => setDiscVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyDiscount()} style={{ flex: 1 }} />
                <span style={{ color: '#777', fontWeight: 800 }}>{discType === 'PERCENT' ? '%' : '€'}</span>
                <button className="cs-confirm" style={{ width: 'auto', padding: '10px 16px', marginTop: 0 }} onClick={applyDiscount}>✓ {t('Appliquer', 'Apply')}</button>
                <button className="cs-btn" onClick={() => { setDiscOpen(false); setDiscVal(''); }}>✕</button>
              </div>
            </div>
          )}
        </div>

        {kind === 'items' ? (
          <div style={{ marginBottom: 12 }}>
            {order.lines.map((l) => (
              <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', borderBottom: '1px solid #eee', cursor: 'pointer' }}>
                <input type="checkbox" checked={sel.includes(l.id)} onChange={() => toggle(l.id)} />
                <span style={{ flex: 1 }}>{l.qty}× {l.nameSnap}</span>
                <b>{euro(lineTotal(l))}</b>
              </label>
            ))}
            <div style={{ textAlign: 'right', marginTop: 8 }}>{t('Sélection', 'Selected')} : <b>{euro(selSum)}</b></div>

            {/* Partager le(s) plat(s) sélectionné(s) entre plusieurs personnes (paiement d'une part). */}
            {selSum > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center', flexWrap: 'wrap', background: '#f7f1e4', borderRadius: 10, padding: '8px 10px' }}>
                <span style={{ color: '#777' }}>🍽 {t('Partager ce plat en', 'Split this dish in')} :</span>
                <button className="cs-btn" onClick={() => setItemParts(Math.max(1, itemParts - 1))}>−</button>
                <input
                  className="inp"
                  type="number"
                  min={1}
                  style={{ width: 64, textAlign: 'center', fontWeight: 800 }}
                  value={itemParts}
                  onChange={(e) => setItemParts(Math.max(1, parseInt(e.target.value, 10) || 1))}
                />
                <button className="cs-btn" onClick={() => setItemParts(itemParts + 1)}>+</button>
                <span style={{ color: '#777' }}>{t('personnes', 'people')}</span>
                {itemParts > 1 && (
                  <span style={{ marginLeft: 'auto', fontWeight: 800, color: '#c60' }}>
                    → {euro(itemsShare)} / {t('pers.', 'each')}
                  </span>
                )}
              </div>
            )}
            {itemParts > 1 && (
              <div style={{ fontSize: '.78rem', color: '#999', marginTop: 6 }}>
                {t('Encaisse une part maintenant. Recommencez pour chaque personne.', 'Charges one share now. Repeat for each person.')}
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
              <span>{t('Montant', 'Amount')} €</span>
              <input className="inp" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: '#777' }}>{t('Partager en', 'Split in')} :</span>
              <button className="cs-btn" onClick={() => setShare(Math.max(1, parts - 1))}>−</button>
              <input
                className="inp"
                type="number"
                min={1}
                style={{ width: 70, textAlign: 'center', fontWeight: 800 }}
                value={parts}
                onChange={(e) => setShare(Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
              <button className="cs-btn" onClick={() => setShare(parts + 1)}>+</button>
              <span style={{ color: '#777' }}>{t('personnes', 'people')}</span>
              <span style={{ color: '#999', fontSize: '.85rem' }}>→ {euro(Math.round(remaining / parts))} / {t('pers.', 'each')}</span>
            </div>
          </>
        )}

        {/* Paiement en cours sur le terminal : on bloque la saisie tant que le client n'a pas répondu. */}
        {term ? (
          <div style={{ background: '#f7f1e4', border: '1.5px solid #e6d3a8', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            {term.status === 'PENDING' ? (
              <>
                <div style={{ fontSize: '2.2rem' }}>💳</div>
                <div style={{ fontWeight: 900, fontSize: '1.1rem', margin: '4px 0' }}>
                  {t('Le client paie sur le terminal', 'Customer is paying on the terminal')}
                </div>
                <div style={{ color: '#c60', fontWeight: 900, fontSize: '1.5rem' }}>{euro(term.amountCents)}</div>
                <button className="cs-btn" style={{ marginTop: 12 }} onClick={cancelTerm}>✕ {t('Annuler', 'Cancel')}</button>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 900, color: '#c0392b', marginBottom: 4 }}>
                  ✕ {term.status === 'CANCELLED' ? t('Paiement annulé', 'Payment cancelled') : t('Paiement refusé', 'Payment declined')}
                </div>
                {term.message && <div style={{ fontSize: '.85rem', color: '#777' }}>{term.message}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="cs-act" style={{ margin: 0, flex: 1 }} onClick={payTerminal}>🔄 {t('Réessayer', 'Try again')}</button>
                  <button className="cs-btn" style={{ flex: 1 }} onClick={() => setTerm(null)}>{t('Autre moyen', 'Other method')}</button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{ fontSize: '.8rem', color: '#888', marginBottom: 6 }}>{t('Encaisser avec', 'Charge with')} :</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {METHODS.map((m) => (
                <button key={m.m} className="cs-act" style={{ margin: 0 }} onClick={() => pay(m.m)}>{m.label}</button>
              ))}
            </div>
            {terminalOk && (
              <button
                className="cs-confirm"
                style={{ width: '100%', marginTop: 8 }}
                onClick={payTerminal}
                title={t('Envoie le montant sur le lecteur de carte', 'Sends the amount to the card reader')}
              >
                💳 {t('Envoyer sur le terminal', 'Send to card terminal')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Modifier / annuler le paiement d'une commande (notifie le manager). */
function VoidModal({ order, t, onClose }: { order: OrderDto; t: TF; onClose: () => void }) {
  const push = useToasts((s) => s.push);
  const [pays, setPays] = useState<{ id: string; method: PaymentMethod; amountCents: number; status: string }[] | null>(null);
  const reload = () => api.listPayments(order.id).then(setPays).catch(() => setPays([]));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (id: string) => {
    if (!confirm(t('Annuler ce paiement ? Le manager sera notifié.', 'Void this payment? Manager will be notified.'))) return;
    try {
      await api.voidPayment(id);
      push(`🗑️ ${t('Paiement annulé — manager notifié', 'Payment voided — manager notified')}`);
      reload();
    } catch (e: any) { push(e.message, 'warn'); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <b style={{ fontSize: '1.2rem', flex: 1 }}>{t('Modifier le paiement', 'Change payment')} · {order.token}</b>
          <button className="cs-btn" onClick={onClose}>✕</button>
        </div>
        {pays === null ? (
          <div className="no-data">{t('Chargement…', 'Loading…')}</div>
        ) : pays.length === 0 ? (
          <div className="no-data">{t('Aucun paiement enregistré.', 'No payment recorded.')}</div>
        ) : (
          pays.map((p) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 6px', borderBottom: '1px solid #eee' }}>
              <span style={{ flex: 1 }}>{PAY_LABEL[p.method] ?? p.method}</span>
              <b>{euro(p.amountCents)}</b>
              {p.status === 'VOID' ? (
                <span style={{ color: '#b23', fontWeight: 700 }}>{t('Annulé', 'Voided')}</span>
              ) : (
                <button className="cs-act" style={{ margin: 0, color: '#b23', width: 'auto', padding: '6px 12px' }} onClick={() => cancel(p.id)}>
                  🗑️ {t('Annuler', 'Void')}
                </button>
              )}
            </div>
          ))
        )}
        <p style={{ color: '#999', fontSize: '.8rem', marginTop: 12 }}>
          ⚠️ {t('Toute annulation est tracée (NF525) et notifiée au manager.', 'Every void is logged (NF525) and the manager is notified.')}
        </p>
      </div>
    </div>
  );
}

/** Options d'impression du justificatif : nombre de couverts + avec/sans détail. */
/** Détail complet d'une commande (lecture seule) — à consulter avant d'encaisser. */
function DetailModal({ order, t, onClose }: { order: OrderDto; t: TF; onClose: () => void }) {
  const paid = order.paidCents >= order.totalCents;
  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <b style={{ fontSize: '1.3rem', flex: 1 }}>{order.token} · {order.clientName || t('Client', 'Customer')}</b>
          <button className="cs-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ color: '#888', fontSize: '.85rem', marginBottom: 4 }}>
          #{order.number} · {order.deliveryAddress ? '📍 ' + order.deliveryAddress + ' · ' : ''}
          {paid ? '✔ ' + t('Payé', 'Paid') : '⏳ ' + t('À régler', 'To pay')}
        </div>
        <div style={{ color: '#888', fontSize: '.85rem', marginBottom: 12 }}>
          🕒 {new Date(order.placedAt ?? order.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          {order.paymentIntent ? ' · 💳 ' + (PAY_LABEL[order.paymentIntent] ?? order.paymentIntent) : ''}
        </div>

        {order.lines.map((l) => (
          <div key={l.id} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0e9da' }}>
            <span style={{ fontWeight: 800, minWidth: 34 }}>{l.qty}×</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{l.nameSnap}</div>
              {l.options.map((o) => (
                <div key={o.id} style={{ fontSize: '.82rem', color: '#7a6a55' }}>
                  + {o.nameSnap}{o.priceDelta ? ` (${euro(o.priceDelta)})` : ''}
                </div>
              ))}
              {l.notes && <div style={{ fontSize: '.82rem', color: '#c0392b' }}>📝 {l.notes}</div>}
            </div>
            <b style={{ whiteSpace: 'nowrap' }}>{euro(lineTotal(l))}</b>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '.9rem', color: '#666' }}>
          <span>{t('Total HT', 'Net')}</span><span>{euro(order.totalCents - order.taxCents)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem', color: '#666' }}>
          <span>{t('TVA', 'VAT')}</span><span>{euro(order.taxCents)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '1.4rem', fontWeight: 900 }}>
          <span>{t('TOTAL', 'TOTAL')}</span><span style={{ color: 'var(--gold, #9B1C31)' }}>{euro(order.totalCents)}</span>
        </div>
        <button className="cs-btn" style={{ marginTop: 14, width: '100%' }} onClick={onClose}>{t('Fermer', 'Close')}</button>
      </div>
    </div>
  );
}

function JustifModal({ order, t, onClose }: { order: OrderDto; t: TF; onClose: () => void }) {
  const push = useToasts((s) => s.push);
  const [covers, setCovers] = useState(1);
  const [detail, setDetail] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [resto, setResto] = useState<RestaurantInfo | null>(null);
  useEffect(() => { api.getSettings().then(setResto).catch(() => {}); }, []);
  const label = `${covers} Repas complet`; // marqué automatiquement selon le nombre choisi
  const paidFull = order.paidCents >= order.totalCents;
  const remain = order.totalCents - order.paidCents;

  const print = async () => {
    setPrinting(true);
    push(t('🖨️ Impression du justificatif…', '🖨️ Printing receipt…'));
    try {
      const r = await api.printReceipt(order.id, 'Caisse', { detail, covers, label });
      push(`✅ ${t('Justificatif imprimé', 'Receipt printed')} (${r.printer})`);
      onClose();
    } catch (e: any) { push(`❌ ${e.message}`, 'warn'); }
    finally { setPrinting(false); }
  };

  const emailIt = async () => {
    const email = prompt(t('Email du client :', 'Customer email:'));
    if (!email || !email.trim()) return;
    push(t('📧 Envoi de l’email…', '📧 Sending email…'));
    try {
      await api.emailReceipt(order.id, email.trim());
      push(`📧 ${t('Justificatif envoyé à', 'Receipt sent to')} ${email.trim()}`);
      onClose();
    } catch (e: any) { push(`❌ ${e.message}`, 'warn'); }
  };

  const btn = (active: boolean): React.CSSProperties => ({
    margin: 0, flex: 1, background: active ? '#7a1f2b' : undefined, color: active ? '#fff' : undefined,
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <b style={{ fontSize: '1.2rem', flex: 1 }}>📄 {t('Aperçu du ticket', 'Ticket preview')} · {order.token}</b>
          <button className="cs-btn" onClick={onClose}>✕</button>
        </div>

        {/* ── Aperçu visuel du ticket (avant impression) ── */}
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 10, padding: 14, marginBottom: 14, fontFamily: 'monospace', fontSize: '.82rem', maxHeight: 320, overflow: 'auto' }}>
          <div style={{ textAlign: 'center', fontWeight: 800, fontSize: '1.1rem' }}>{resto?.name ?? 'TAJ FUSION'}</div>
          {resto && <div style={{ textAlign: 'center', color: '#888', fontSize: '.72rem' }}>{resto.tagline}<br />{resto.address}<br />{resto.phone}</div>}
          <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
          <div style={{ fontWeight: 700 }}>{t('Ticket', 'Ticket')} {order.token}</div>
          <div style={{ color: '#888' }}>{new Date(order.createdAt).toLocaleString('fr-FR')}</div>
          {order.clientName && <div>{t('Client', 'Customer')} : {order.clientName}</div>}
          <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
          {detail ? order.lines.map((l) => (
            <div key={l.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{l.qty}× {l.nameSnap}</span><span>{euro(lineTotal(l))}</span></div>
              {l.options.length > 0 && <div style={{ color: '#999', fontSize: '.72rem' }}>+ {l.options.map((o) => o.nameSnap).join(', ')}</div>}
            </div>
          )) : (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{label}</span><span>{euro(order.totalCents)}</span></div>
          )}
          <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total HT</span><span>{euro(order.totalCents - order.taxCents)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>TVA</span><span>{euro(order.taxCents)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1rem' }}><span>TTC</span><span>{euro(order.totalCents)}</span></div>
          {!paidFull && order.paidCents > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#2a7' }}><span>{t('Déjà payé', 'Already paid')}</span><span>{euro(order.paidCents)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#c33', fontWeight: 800 }}><span>{t('Reste à payer', 'Remaining')}</span><span>{euro(remain)}</span></div>
            </>
          )}
          <div style={{ textAlign: 'center', marginTop: 8, fontWeight: 800, color: paidFull ? '#2a7' : '#c33' }}>{paidFull ? '✔ PAYÉ' : '⚠ RESTE À PAYER'}</div>
          {order.fiscalRef && <div style={{ textAlign: 'center', marginTop: 6, fontSize: '.7rem', color: '#999', fontFamily: 'monospace' }}>Trace NF525 : {order.fiscalRef}</div>}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '.8rem', color: '#888', marginBottom: 6 }}>{t('Contenu du ticket', 'Ticket content')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cs-act" style={btn(detail)} onClick={() => setDetail(true)}>🧾 {t('Avec détail', 'Detailed')}</button>
            <button className="cs-act" style={btn(!detail)} onClick={() => setDetail(false)}>📃 {t('Sans détail', 'Total only')}</button>
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: '.8rem', color: '#888', marginBottom: 6 }}>{t('Nombre de repas', 'Number of meals')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="cs-btn" onClick={() => setCovers((c) => Math.max(1, c - 1))}>−</button>
            <b style={{ fontSize: '1.4rem', minWidth: 40, textAlign: 'center' }}>{covers}</b>
            <button className="cs-btn" onClick={() => setCovers((c) => c + 1)}>+</button>
            <span style={{ color: '#7a1f2b', fontWeight: 700 }}>→ {label}</span>
          </div>
          {covers > 1 && <div style={{ color: '#999', fontSize: '.85rem', marginTop: 6 }}>{euro(Math.round(order.totalCents / covers))} / {t('repas', 'meal')}</div>}
        </div>

        <button className="cs-confirm" style={{ width: '100%' }} disabled={printing} onClick={print}>🖨️ {t('Imprimer le justificatif', 'Print receipt')}</button>
        <button className="cs-act" style={{ width: '100%', marginTop: 8 }} onClick={emailIt}>📧 {t('Envoyer par email', 'Send by email')}</button>
      </div>
    </div>
  );
}
